import styled from '@emotion/styled';
import { Typography } from '@innovamat/glimmer-components';

import { CustomCheckBox } from './custom-checkbox';

const BlockContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleWithSelectAll = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  .select-all-checkbox {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover .select-all-checkbox {
    opacity: 1;
  }
`;

const CheckBoxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

type CheckboxOption = {
  name?: string;
  description?: string;
  label?: string;
  value?: string;
};

type CheckboxSectionProps = {
  title: string;
  options: CheckboxOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  getOptionKey: (option: CheckboxOption) => string;
  getOptionLabel: (option: CheckboxOption) => string;
};

function CheckboxSection({
  title,
  options,
  selectedValues,
  onSelectionChange,
  getOptionKey,
  getOptionLabel,
}: CheckboxSectionProps): JSX.Element {
  const allSelected =
    options.length > 0 &&
    options.every((option) => selectedValues.includes(getOptionKey(option)));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allValues = options.map(getOptionKey);
      onSelectionChange(allValues);
    } else {
      onSelectionChange([]);
    }
  };

  const handleOptionChange = (optionKey: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedValues, optionKey]);
    } else {
      onSelectionChange(selectedValues.filter((value) => value !== optionKey));
    }
  };

  return (
    <BlockContainer>
      <TitleWithSelectAll>
        <Typography.H4>{title}</Typography.H4>
        <div className="select-all-checkbox">
          <CustomCheckBox
            text="Select All"
            checked={allSelected}
            onCheck={handleSelectAll}
          />
        </div>
      </TitleWithSelectAll>
      <CheckBoxes>
        {options.map((option) => {
          const optionKey = getOptionKey(option);
          const optionLabel = getOptionLabel(option);

          return (
            <CustomCheckBox
              key={optionKey}
              text={optionLabel}
              checked={selectedValues.includes(optionKey)}
              onCheck={(checked) => handleOptionChange(optionKey, checked)}
            />
          );
        })}
      </CheckBoxes>
    </BlockContainer>
  );
}

export { CheckboxSection };
export type { CheckboxOption, CheckboxSectionProps };
